import { useContext } from "react";
import { Trans } from "@lingui/macro";
import {
  ExportCreateResponseBody,
  useExportCreate,
} from "@gocardless/api/dashboard/export";
import { ExportCreateRequestBody } from "@gocardless/api/dashboard/types";
import { getErrorsFromErrorResponse } from "@gocardless/api/utils";

import { useToastNotification } from "../../../hooks/useToastNotification";

import { GlobalState } from "src/components/global-state/GlobalStateProvider";
import { ExportsLocalStorageCache } from "src/components/ui/export-centre/ExportCentreConnect";

export const useDashboardExportCreate = ({
  exportsType,
  filterParams,
  isFlexibleExportsAllowed = false,
  onSuccess,
  onError,
}: {
  exportsType: ExportCreateRequestBody["export_type"];
  filterParams?: ExportCreateRequestBody["filter_params"];
  isFlexibleExportsAllowed?: boolean;
  onSuccess?: (data: ExportCreateResponseBody) => void;
  onError?: (error: Error) => void;
}) => {
  const { exports, setExports } = useContext(GlobalState);

  const { triggerErrorNotification, triggerSuccessNotification } =
    useToastNotification();

  const defaultExportErrorNotification = () => {
    triggerErrorNotification({
      title: <Trans id="Error">Error</Trans>,
      message: (
        <Trans id="exports.create.error">Failed to create export.</Trans>
      ),
    });
  };

  const exportErrorNotification = (message: string) => {
    triggerErrorNotification({
      title: <Trans id="Error">Error</Trans>,
      message,
    });
  };

  const exportSuccessNotification = () => {
    triggerSuccessNotification({
      title: <Trans id="Export">Export</Trans>,
      message: (
        <Trans id="export.create.success">
          Your export is processing, please see &apos;Exports&apos; below for
          more details.
        </Trans>
      ),
    });
  };

  const [submit, { isMutating }] = useExportCreate({
    onError: (error) => {
      getErrorsFromErrorResponse(error)
        .then((errors) => {
          if (errors && errors.length > 0) {
            errors.forEach(({ message }) => {
              exportErrorNotification(message);
            });
          }
        })
        .catch(defaultExportErrorNotification);

      onError?.(error);
    },
    onSuccess: (response) => {
      if (response) {
        exportSuccessNotification();
        // update localstorage cache and show export centre.
        onExportCreate(response);

        onSuccess?.(response);
      }
    },
  });

  const onExportCreate = (exportResponse: ExportCreateResponseBody) => {
    const exportsCopy: ExportsLocalStorageCache = { ...exports };
    if (exportResponse.exports?.id) {
      exportsCopy.pendingExports = exportsCopy.pendingExports.concat(
        exportResponse.exports?.id
      );
      exportsCopy.isShowingCentre = true;
      setExports(exportsCopy);
    }
  };

  const handleCreateExport = (
    selectedHeadings?: ExportCreateRequestBody["headers"],
    includeMetadata: ExportCreateRequestBody["include_metadata"] = false
  ) => {
    const exportsCreate: ExportCreateRequestBody = {
      export_type: exportsType,
      filter_params: filterParams,
    };

    if (isFlexibleExportsAllowed) {
      exportsCreate.headers = selectedHeadings;
      exportsCreate.include_metadata = includeMetadata;
    }
    submit(exportsCreate);
  };

  return { submit: handleCreateExport, isSubmitting: isMutating };
};
